// packages/frontend/src/stores/authContextBridge.ts
// Bridge to access AuthContext from outside React components (for Zustand stores and services)

import type { User, Therapist } from '@msb/shared';
import type { User as FirebaseUser } from 'firebase/auth';

interface AuthState {
  firebaseUser: FirebaseUser | null;
  currentUser: User | null;
  currentTherapist: Therapist | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

// Global auth state that will be updated by AuthContext
let globalAuthState: AuthState = {
  firebaseUser: null,
  currentUser: null,
  currentTherapist: null,
  isAuthenticated: false,
  loading: true,
  error: null,
};

// Subscribers for auth state changes
const subscribers = new Set<(state: AuthState) => void>();

/**
 * Update global auth state (called by AuthContext)
 */
export function updateGlobalAuthState(state: AuthState): void {
  globalAuthState = state;
  
  // Notify all subscribers
  subscribers.forEach(callback => callback(state));
}

/**
 * Get current auth state (for use in services and stores)
 */
export function getGlobalAuthState(): AuthState {
  return globalAuthState;
}

/**
 * Subscribe to auth state changes
 */
export function subscribeToAuthState(callback: (state: AuthState) => void): () => void {
  subscribers.add(callback);
  
  // Return unsubscribe function
  return () => {
    subscribers.delete(callback);
  };
}

/**
 * Get current Firebase user (for token retrieval)
 */
export function getCurrentFirebaseUser(): FirebaseUser | null {
  return globalAuthState.firebaseUser;
}

/**
 * Check if user is authenticated
 */
export function isUserAuthenticated(): boolean {
  return globalAuthState.isAuthenticated;
}

